import { useEffect, useState } from "react";
import { Globe } from "lucide-react";
import { useLocale } from "@/contexts/LocaleContext";

function FxBadge() {
    const { rateSource, currency } = useLocale();
    if (currency === "INR") return null;
    const live = rateSource === "live";
    return (
        <span
            className="lcs-badge"
            style={{
                marginLeft: 6, padding: "2px 6px", borderRadius: 999,
                fontSize: 9.5, fontWeight: 700, letterSpacing: "0.12em",
                background: live ? "rgba(41,197,138,0.18)" : "rgba(246,163,73,0.18)",
                color: live ? "#29C58A" : "#F6A349",
            }}
            data-testid="lcs-fx-badge"
            title={live ? "Live rate from public FX feed" : "Fallback to static Feb-2026 map (provider offline)"}
        >
            {live ? "LIVE" : "STATIC"}
        </span>
    );
}

/* ═════════════════════════════════════════════════════════════════════
   Language + Currency selector for the public nav.
   • Remembers choice in localStorage under vault.locale.
   • Emits `vault:locale-change` so any downstream consumers can react.
   • Does NOT perform live-FX conversion — the billing currency is the
     one the user checks out in, and is shown clearly at checkout.
   ═════════════════════════════════════════════════════════════════════ */

const LANGS = [
    { code: "en", label: "English" },
    { code: "hi", label: "हिन्दी" },
    { code: "es", label: "Español" },
    { code: "fr", label: "Français" },
];

const CURRENCIES = [
    { code: "INR", label: "₹ INR" },
    { code: "USD", label: "$ USD" },
    { code: "EUR", label: "€ EUR" },
    { code: "GBP", label: "£ GBP" },
    { code: "AED", label: "AED" },
    { code: "SGD", label: "S$ SGD" },
];

const KEY = "vault.locale";

export default function LangCurrencySelector() {
    const [open, setOpen] = useState(false);
    const [locale, setLocale] = useState(() => {
        try { return JSON.parse(localStorage.getItem(KEY) || "{}"); } catch { return {}; }
    });
    const lang = locale.lang || "en";
    const curr = locale.currency || "INR";

    useEffect(() => {
        try { localStorage.setItem(KEY, JSON.stringify(locale)); } catch { /* noop */ }
        window.dispatchEvent(new CustomEvent("vault:locale-change", { detail: locale }));
    }, [locale]);

    const langLbl = LANGS.find((l) => l.code === lang)?.label || "English";
    const currLbl = CURRENCIES.find((c) => c.code === curr)?.label || "₹ INR";

    return (
        <div className="lcs-root" data-testid="lang-currency">
            <style>{styles}</style>
            <button className="lcs-btn" onClick={() => setOpen((o) => !o)} data-testid="lcs-toggle">
                <Globe size={14} />
                <span>{lang.toUpperCase()} · {curr}</span>
                <FxBadge />
            </button>
            {open && (
                <div className="lcs-panel" role="dialog">
                    <div className="lcs-section">
                        <div className="lcs-title">Language</div>
                        {LANGS.map((l) => (
                            <button key={l.code}
                                    className={`lcs-opt ${lang === l.code ? "lcs-opt--active" : ""}`}
                                    onClick={() => setLocale({ ...locale, lang: l.code })}
                                    data-testid={`lcs-lang-${l.code}`}
                            >{l.label} <span className="lcs-code">{l.code.toUpperCase()}</span></button>
                        ))}
                        <div className="lcs-hint">English is fully translated. Other languages are being rolled out.</div>
                    </div>
                    <div className="lcs-section">
                        <div className="lcs-title">Display currency</div>
                        {CURRENCIES.map((c) => (
                            <button key={c.code}
                                    className={`lcs-opt ${curr === c.code ? "lcs-opt--active" : ""}`}
                                    onClick={() => setLocale({ ...locale, currency: c.code })}
                                    data-testid={`lcs-curr-${c.code}`}
                            >{c.label}</button>
                        ))}
                        <div className="lcs-hint">Billing currency is set at checkout and shown clearly there — VAULT does not auto-convert bills using FX rates.</div>
                    </div>
                </div>
            )}
        </div>
    );
}

const styles = `
.lcs-root { position: relative; }
.lcs-btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 12px; border-radius: 999px; background: rgba(246,243,236,0.06); color: rgba(246,243,236,0.85); font-size: 12.5px; font-weight: 600; border: 1px solid rgba(246,243,236,0.1); cursor: pointer; }
.lcs-btn:hover { border-color: rgba(246,243,236,0.25); }
.lcs-panel { position: absolute; top: calc(100% + 8px); right: 0; z-index: 50; background: #0B1122; color: #F6F3EC; border: 1px solid rgba(246,243,236,0.1); border-radius: 16px; width: 280px; padding: 16px; box-shadow: 0 30px 60px -30px rgba(0,0,0,0.6); }
.lcs-section + .lcs-section { margin-top: 16px; border-top: 1px solid rgba(246,243,236,0.06); padding-top: 16px; }
.lcs-title { font-size: 10.5px; letter-spacing: 0.22em; font-weight: 700; text-transform: uppercase; color: rgba(246,243,236,0.5); margin-bottom: 8px; }
.lcs-opt { display: flex; align-items: center; justify-content: space-between; width: 100%; padding: 8px 10px; border-radius: 8px; background: transparent; border: none; color: rgba(246,243,236,0.85); font-size: 13px; cursor: pointer; }
.lcs-opt:hover { background: rgba(246,243,236,0.04); }
.lcs-opt--active { background: rgba(198,168,107,0.14); color: #C6A86B; }
.lcs-code { font-size: 10px; color: rgba(246,243,236,0.4); }
.lcs-hint { margin-top: 10px; font-size: 11px; color: rgba(246,243,236,0.5); line-height: 1.5; }
`;
